import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useCacheDemo } from '../hooks/useCacheDemo';
import { cacheFormSchema, type CacheFormValues } from '../schema/showcaseSchema';
import type { CacheDemoResponse } from '../types/showcase';

export function CachePanel() {
  const { t } = useTranslation();
  const { mutate, isPending } = useCacheDemo();
  const [call1, setCall1] = useState<CacheDemoResponse | null>(null);
  const [call2, setCall2] = useState<CacheDemoResponse | null>(null);
  const [callCount, setCallCount] = useState(0);
  const [callError, setCallError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<CacheFormValues>({
    resolver: zodResolver(cacheFormSchema),
    defaultValues: { key: 'hello' },
  });

  function onSubmit(values: CacheFormValues) {
    setCallError(null);
    mutate(values.key, {
      onSuccess: (data) => {
        const nextCount = callCount + 1;
        setCallCount(nextCount);
        if (nextCount === 1) {
          setCall1(data);
          setCall2(null);
        } else {
          setCall2(data);
        }
      },
      onError: (err) => {
        setCallError(err.message);
      },
    });
  }

  function handleReset() {
    setCall1(null);
    setCall2(null);
    setCallCount(0);
    setCallError(null);
  }

  const maxDuration = Math.max(
    call1?.durationMs ?? 0,
    call2?.durationMs ?? 0,
    1
  );

  return (
    <section aria-labelledby="cache-panel-title">
      <h2
        id="cache-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-4)',
          marginTop: 0,
        }}
      >
        {t('showcase.cache.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.cache.subtitle')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="cache-key"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.cache.key')}
          </label>
          <input
            id="cache-key"
            type="text"
            placeholder="hello"
            aria-label={t('showcase.cache.key')}
            aria-invalid={!!errors.key}
            aria-describedby={errors.key ? 'cache-key-error' : undefined}
            {...register('key')}
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: `1px solid ${errors.key ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: isPending ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              boxSizing: 'border-box',
            }}
          />
          {errors.key && (
            <p
              id="cache-key-error"
              role="alert"
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--spacing-1)' }}
            >
              {errors.key.message}
            </p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)' }}>
          <button
            type="submit"
            disabled={isPending || !isValid}
            aria-busy={isPending}
            style={{
              padding: 'var(--spacing-3) var(--spacing-6)',
              background: isPending ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {isPending && (
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'var(--color-text-inverse)',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            )}
            {isPending
              ? t('showcase.cache.loading')
              : callCount === 0
              ? t('showcase.cache.call0')
              : callCount === 1
              ? t('showcase.cache.call1')
              : t('showcase.cache.callAgain')}
          </button>
          {(call1 || call2) && (
            <button
              type="button"
              onClick={handleReset}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('showcase.common.reset')}
            </button>
          )}
        </div>
      </form>

      {/* Progress bar while loading */}
      {isPending && (
        <div
          aria-label={t('showcase.cache.callProgress')}
          role="progressbar"
          aria-valuenow={undefined}
          style={{
            width: '100%',
            height: 4,
            background: 'var(--color-surface-border)',
            borderRadius: 'var(--radius-full)',
            overflow: 'hidden',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: '40%',
              height: '100%',
              background: 'var(--color-brand-primary)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </div>
      )}

      {/* No calls yet */}
      {!call1 && !isPending && (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-4)' }}>
          {t('showcase.cache.noCallsYet')}
        </p>
      )}

      {/* Error */}
      {callError && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-danger)',
            fontSize: 'var(--font-size-sm)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          {callError}
        </div>
      )}

      {/* Call results */}
      {(call1 || call2) && (
        <div
          role="region"
          aria-label={t('showcase.cache.resultLabel')}
          style={{ marginBottom: 'var(--spacing-4)' }}
        >
          <h3
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 700,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-3)',
            }}
          >
            {t('showcase.cache.compare')}
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)' }}>
            {[
              { label: t('showcase.cache.call1Label'), data: call1, isHit: false },
              { label: t('showcase.cache.call2Label'), data: call2, isHit: true },
            ].map(({ label, data: callData, isHit }) => (
              <div
                key={label}
                style={{
                  border: `1px solid ${
                    callData
                      ? isHit
                        ? 'var(--color-success)'
                        : 'var(--color-warning)'
                      : 'var(--color-surface-border)'
                  }`,
                  borderRadius: 'var(--radius-md)',
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-surface-muted)',
                }}
              >
                <p
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    fontWeight: 700,
                    color: callData
                      ? isHit
                        ? 'var(--color-success)'
                        : 'var(--color-warning)'
                      : 'var(--color-text-muted)',
                    marginBottom: 'var(--spacing-2)',
                  }}
                >
                  {label}
                </p>
                {callData ? (
                  <>
                    <p
                      style={{
                        fontSize: 'var(--font-size-2xl)',
                        fontWeight: 700,
                        color: 'var(--color-text-primary)',
                        margin: '0 0 var(--spacing-1)',
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      {callData.durationMs}ms
                    </p>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: callData.cacheHit ? 'var(--color-success)' : 'var(--color-warning)',
                        margin: '0 0 var(--spacing-2)',
                        fontFamily: 'var(--font-family-mono)',
                      }}
                    >
                      cacheHit: {String(callData.cacheHit)}
                    </p>
                    {/* Mini bar */}
                    <div
                      style={{
                        width: '100%',
                        height: 8,
                        background: 'var(--color-surface-border)',
                        borderRadius: 'var(--radius-full)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        aria-hidden="true"
                        style={{
                          width: `${Math.max(2, Math.round((callData.durationMs / maxDuration) * 100))}%`,
                          height: '100%',
                          background: callData.cacheHit
                            ? 'var(--color-success)'
                            : 'var(--color-warning)',
                          borderRadius: 'var(--radius-full)',
                          transition: 'width 0.4s ease',
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', margin: 0 }}>
                    {t('showcase.cache.notCalledYet')}
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Key/value */}
          {call1 && (
            <div style={{ marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              key: <code style={{ fontFamily: 'var(--font-family-mono)' }}>{call1.key}</code>
              {' '} → value: <code style={{ fontFamily: 'var(--font-family-mono)' }}>{call1.value}</code>
            </div>
          )}
        </div>
      )}

      {/* Learning */}
      <div
        style={{
          padding: 'var(--spacing-3)',
          background: 'var(--color-surface-elevated)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
        }}
      >
        <span aria-hidden="true" style={{ marginRight: 'var(--spacing-2)' }}>📚</span>
        <strong>{t('showcase.panel.learning')}</strong> {t('showcase.cache.learning')}
      </div>
    </section>
  );
}
